import { useMutation, useQuery } from '@apollo/client/react';
import { TeamSpot } from '@optools/components/team-spot';
import { Button } from '@optools/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Separator } from '@optools/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@optools/components/ui/sheet';
import { Skeleton } from '@optools/components/ui/skeleton';
import { UPDATE_PIPELINE_CONFIG } from '@optools/services/operator-gateway/pipeline-config/mutations';
import { GET_PIPELINE_CONFIG_DETAIL } from '@optools/services/operator-gateway/pipeline-config/queries';
import { Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { PipelineConfigForm, PipelineConfigFormValues } from './pipeline-config-form';

export interface PipelineConfigSheetProps {
  configuredRegions: Region[];
  onOpenChange: (open: boolean) => void;
  open: boolean;
  teamId: number | null;
}

export function PipelineConfigSheet({
  configuredRegions,
  onOpenChange,
  open,
  teamId,
}: PipelineConfigSheetProps) {
  const [editMode, setEditMode] = useState(false);
  const [region, setRegion] = useState<Region>(Region.EAST);

  // Maintains current team reference when the sheet is closing to avoid flash of content
  const [_teamId, _setTeamId] = useState(teamId);

  useEffect(() => {
    if (open) {
      _setTeamId(teamId);
      setRegion(configuredRegions.includes(Region.EAST) ? Region.EAST : configuredRegions[0]);
    }
  }, [open, teamId, configuredRegions]);

  const { data, loading: detailLoading } = useQuery(GET_PIPELINE_CONFIG_DETAIL, {
    skip: !open || teamId == null,
    variables: { region, teamId: teamId ?? 0 },
  });

  const config = data?.pipelineConfigs[0];

  const notifyUpdateFailure = () =>
    toast.error('Pipeline config failed to update', {
      description: 'Please try again.',
      position: 'bottom-left',
    });

  const [updatePipelineConfigMutation, { loading }] = useMutation(UPDATE_PIPELINE_CONFIG, {
    context: { skipErrorNotification: true },
    onCompleted: ({ updatePipelineConfig }) => {
      if (updatePipelineConfig) {
        toast.success('Pipeline config successfully updated', { position: 'bottom-left' });
        setEditMode(false);
      } else {
        notifyUpdateFailure();
      }
    },
    onError: notifyUpdateFailure,
  });

  const handleSubmit = (values: PipelineConfigFormValues) => {
    if (_teamId == null) return;

    updatePipelineConfigMutation({
      variables: { pipelineConfig: values, region, teamId: _teamId },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (loading) return;
    setEditMode(false);
    onOpenChange(open);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <TeamSpot id={_teamId} showAbbreviation={false} size={32} />
              <span>Team ID: {_teamId}</span>
            </div>
            <Select
              disabled={editMode || loading || configuredRegions.length <= 1}
              value={region}
              onValueChange={(value) => setRegion(value as Region)}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem disabled={!configuredRegions.includes(Region.EAST)} value={Region.EAST}>
                  {Region.EAST}
                </SelectItem>
                <SelectItem
                  disabled={!configuredRegions.includes(Region.CENTRAL)}
                  value={Region.CENTRAL}>
                  {Region.CENTRAL}
                </SelectItem>
              </SelectContent>
            </Select>
            <span>Config ID: {config?.id}</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Edit the pipeline config</SheetDescription>
          <Separator className="mt-4" />
        </SheetHeader>

        {detailLoading ? (
          <div className="space-y-4" data-testid="pipeline-config-loading">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={`pipeline-config-skeleton-${index}`} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            <PipelineConfigForm
              key={`${_teamId}-${region}`}
              config={config ?? {}}
              editMode={editMode}
              loading={loading}
              onCancel={() => setEditMode(false)}
              onSubmit={handleSubmit}
            />

            {!editMode && (
              <div className="mt-12 flex justify-end">
                <Button className="w-32" onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
